import { DispatchManifestStopDetailStatus, ProbillStatus } from "app/data/city-delivery/dispatch-board/models";
import { PickupStatus } from "app/data/common/route";
import type {
  DriverGroup,
  PickupGroupBlock,
  PickupInGroupRow,
} from "./usePickupAssignmentDriversColumn";
import {
  aggregateUnassignedGroupStatus,
} from "../unassignedPickupTypes";
import type {
  UnassignedGroupByMode,
  UnassignedPickup,
  UnassignedRoute,
  UnassignedStopGroup,
} from "../unassignedPickupTypes";

export type MapDispatchColumns = {
  drivers: DriverGroup[];
  unassigned: UnassignedRoute[];
};

type RemovedFromDriver = {
  row: PickupInGroupRow;
  group: PickupGroupBlock;
};

function pickupGroupAggregateStatus(completed: number, total: number): DispatchManifestStopDetailStatus {
  if (total === 0) return DispatchManifestStopDetailStatus.PENDING;
  if (completed === total) return DispatchManifestStopDetailStatus.COMPLETED;
  if (completed > 0) return DispatchManifestStopDetailStatus.IN_PROGRESS;
  return DispatchManifestStopDetailStatus.PENDING;
}

function pickupInGroupRowFromUnassigned(p: UnassignedPickup): PickupInGroupRow {
  const weightNum = Number(p.weight);
  return {
    id: p.id,
    pickupNumber: p.puNumber || p.id,
    probillNumber: p.probillNumber,
    shipperName: p.shipperName,
    assignedRelative: "—",
    status: PickupStatus.ASSIGNED,
    pieces: p.pieces,
    weight: Number.isFinite(weightNum) ? weightNum : p.totalItemsWeight,
    timeRange: p.timeRange,
    pickupDate: p.pickupDateForTiming,
    readyTime: p.readyTimeForTiming,
    closeTime: p.closeTimeForTiming,
    rollsUnits: p.rollsUnits,
    palletsUnits: p.palletsUnits,
    totalItemsWeight: p.totalItemsWeight,
    pickupWindowLine: p.pickupWindowLine,
    shipperContactLine: p.shipperContactLine,
    consigneeName: p.consigneeName,
    consigneeAddressLine: p.consigneeAddressLine,
    consigneeContactLine: p.consigneeContactLine,
    consigneeSummaryLabel: p.consigneeSummaryLabel,
    itemLines: p.itemLines,
  };
}

function unassignedFromGroupRow(row: PickupInGroupRow, group: PickupGroupBlock): UnassignedPickup {
  return {
    id: row.id,
    title: row.shipperName,
    address: group.shipperAddress,
    timeRange: row.timeRange,
    pickupDateForTiming: row.pickupDate,
    readyTimeForTiming: row.readyTime,
    closeTimeForTiming: row.closeTime,
    type: "Pickup",
    puNumber: row.pickupNumber,
    consigneeName: row.consigneeName,
    pieces: row.pieces,
    weight: String(row.weight),
    eta: row.pickupDate,
    shipperName: row.shipperName,
    date: row.pickupDate,
    status: [PickupStatus.UNASSIGNED],
    probillStatus: null,
    zip: "",
    probillNumber: row.probillNumber,
    rollsUnits: row.rollsUnits,
    palletsUnits: row.palletsUnits,
    totalItemsWeight: row.totalItemsWeight,
    pickupWindowLine: row.pickupWindowLine,
    shipperContactLine: row.shipperContactLine,
    consigneeAddressLine: row.consigneeAddressLine,
    consigneeContactLine: row.consigneeContactLine,
    itemLines: row.itemLines,
    consigneeSummaryLabel: row.consigneeSummaryLabel,
  };
}

function recalcDriverStats(driver: DriverGroup): DriverGroup {
  let numberOfPickups = 0;
  let pickupsCompleted = 0;
  let deliveryProbillCount = 0;
  let deliveredProbillsCount = 0;
  for (const s of driver.stops) {
    if (s.kind === "pickup_group") {
      numberOfPickups += s.totalPickups;
      pickupsCompleted += s.completedPickups;
    } else if (s.kind === "manifest_stop") {
      deliveryProbillCount += s.probills.length;
      deliveredProbillsCount += s.probills.filter((p) => p.probillStatus === ProbillStatus.DELIVERED).length;
    }
  }
  const progressTotal = numberOfPickups + deliveryProbillCount;
  const progressDone = pickupsCompleted + deliveredProbillsCount;
  return {
    ...driver,
    numberOfPickups,
    pickupsCompleted,
    numberOfDeliveries: deliveryProbillCount,
    deliveriesCompleted: deliveredProbillsCount,
    progressLabel: progressTotal === 0 ? "—" : `${progressDone}/${progressTotal} done`,
  };
}

function removePickupsFromUnassigned(
  unassigned: UnassignedRoute[],
  pickupIds: Set<string>
): { unassigned: UnassignedRoute[]; removed: UnassignedPickup[]; lat?: number; lng?: number } {
  const removed: UnassignedPickup[] = [];
  let lat: number | undefined;
  let lng: number | undefined;
  const routes = unassigned
    .map((route) => {
      const stops = route.stops
        .map((stop) => {
          const pickups = stop.pickups.filter((p) => {
            if (pickupIds.has(p.id)) {
              removed.push(p);
              if (lat == null && stop.lat != null) lat = stop.lat;
              if (lng == null && stop.lng != null) lng = stop.lng;
              return false;
            }
            return true;
          });
          if (pickups.length === stop.pickups.length) return stop;
          return {
            ...stop,
            pickups,
            groupStopStatus: aggregateUnassignedGroupStatus(pickups),
          };
        })
        .filter((s) => s.pickups.length > 0);
      const count = stops.reduce((sum, s) => sum + s.pickups.length, 0);
      return { ...route, stops, count };
    })
    .filter((r) => r.stops.length > 0);
  return { unassigned: routes, removed, lat, lng };
}

function removePickupsFromDrivers(
  drivers: DriverGroup[],
  pickupIds: Set<string>
): { drivers: DriverGroup[]; removed: RemovedFromDriver[] } {
  const removed: RemovedFromDriver[] = [];
  const nextDrivers = drivers.map((driver) => {
    let changed = false;
    const stops = driver.stops
      .map((stop) => {
        if (stop.kind !== "pickup_group") return stop;
        const group = stop as PickupGroupBlock;
        const pickups = group.pickups.filter((p) => {
          if (pickupIds.has(p.id)) {
            removed.push({ row: p, group });
            return false;
          }
          return true;
        });
        if (pickups.length === group.pickups.length) return stop;
        changed = true;
        if (pickups.length === 0) return null;
        const completed = pickups.filter((p) => p.status === PickupStatus.COMPLETED).length;
        return {
          ...group,
          pickups,
          totalPickups: pickups.length,
          completedPickups: completed,
          groupStopStatus: pickupGroupAggregateStatus(completed, pickups.length),
        };
      })
      .filter((s): s is DriverGroup["stops"][number] => s != null);
    if (!changed) return driver;
    return recalcDriverStats({ ...driver, stops });
  });
  return { drivers: nextDrivers, removed };
}

type PickupPlacement = {
  rows: PickupInGroupRow[];
  shipperAddress: string;
  shipperLat?: number;
  shipperLng?: number;
};

function addPlacementToDriver(
  drivers: DriverGroup[],
  driverIdNum: number,
  placement: PickupPlacement
): DriverGroup[] {
  const driverIdStr = String(driverIdNum);
  return drivers.map((driver) => {
    if (driver.driverId !== driverIdStr) return driver;
    const { rows, shipperAddress, shipperLat, shipperLng } = placement;
    if (rows.length === 0) return driver;

    let merged = false;
    const stops = driver.stops.map((stop) => {
      if (stop.kind !== "pickup_group" || merged) return stop;
      const group = stop as PickupGroupBlock;
      if (group.shipperAddress !== shipperAddress) return stop;
      const pickups = [...group.pickups, ...rows];
      merged = true;
      const completed = pickups.filter((p) => p.status === PickupStatus.COMPLETED).length;
      return {
        ...group,
        pickups,
        totalPickups: pickups.length,
        completedPickups: completed,
        groupStopStatus: pickupGroupAggregateStatus(completed, pickups.length),
        shipperLat: group.shipperLat ?? shipperLat,
        shipperLng: group.shipperLng ?? shipperLng,
      };
    });

    if (merged) {
      return recalcDriverStats({ ...driver, stops });
    }

    const completed = rows.filter((p) => p.status === PickupStatus.COMPLETED).length;
    const safeKey = shipperAddress.replace(/[^\w-]+/g, "-").slice(0, 48);
    const newGroup: PickupGroupBlock = {
      kind: "pickup_group",
      id: `pickup-group-${driverIdNum}-map-${safeKey}-${Date.now()}`,
      shipperAddress,
      driverListId: `driver-${driverIdNum}`,
      pickups: rows,
      totalPickups: rows.length,
      completedPickups: completed,
      groupStopStatus: pickupGroupAggregateStatus(completed, rows.length),
      shipperLat,
      shipperLng,
    };
    return recalcDriverStats({ ...driver, stops: [...stops, newGroup] });
  });
}

function addPickupsToUnassigned(
  unassigned: UnassignedRoute[],
  batches: Array<{ pickups: UnassignedPickup[]; lat?: number; lng?: number }>
): UnassignedRoute[] {
  if (batches.length === 0) return unassigned;

  const newStops: UnassignedStopGroup[] = batches.map((batch, idx) => ({
    id: `unassign-stop-map-${Date.now()}-${idx}`,
    pickups: batch.pickups,
    groupStopStatus: aggregateUnassignedGroupStatus(batch.pickups),
    lat: batch.lat,
    lng: batch.lng,
  }));

  if (unassigned.length === 0) {
    const count = newStops.reduce((sum, s) => sum + s.pickups.length, 0);
    return [{ id: "map-unassigned", name: "Unassigned", count, stops: newStops }];
  }

  const [first, ...rest] = unassigned;
  const stops = [...first.stops, ...newStops];
  const count = stops.reduce((sum, s) => sum + s.pickups.length, 0);
  return [{ ...first, stops, count }, ...rest];
}

/**
 * Applies an optimistic column update for map assign / reassign / unassign.
 * Returns null when no matching pickups were found in the current columns.
 */
export function applyOptimisticMapAssign(
  columns: MapDispatchColumns,
  pickupIds: string[],
  driverId: number | null,
  status: PickupStatus,
  _groupBy: UnassignedGroupByMode = "shipper_address"
): MapDispatchColumns | null {
  const ids = new Set(pickupIds.filter(Boolean));
  if (ids.size === 0) return null;

  if (status === PickupStatus.ASSIGNED && driverId != null) {
    const { unassigned, removed: fromUnassigned, lat: unassignedLat, lng: unassignedLng } =
      removePickupsFromUnassigned(columns.unassigned, ids);
    const { drivers, removed: fromDrivers } = removePickupsFromDrivers(columns.drivers, ids);

    const rows: PickupInGroupRow[] = [
      ...fromUnassigned.map(pickupInGroupRowFromUnassigned),
      ...fromDrivers.map((x) => ({ ...x.row, status: PickupStatus.ASSIGNED })),
    ];
    if (rows.length === 0) return null;

    const addressSource =
      fromUnassigned[0]?.address ??
      fromDrivers[0]?.group.shipperAddress ??
      rows[0]?.shipperName ??
      "—";
    const lat = unassignedLat ?? fromDrivers[0]?.group.shipperLat;
    const lng = unassignedLng ?? fromDrivers[0]?.group.shipperLng;

    const driversNext = addPlacementToDriver(drivers, driverId, {
      rows,
      shipperAddress: addressSource,
      shipperLat: lat,
      shipperLng: lng,
    });

    return { drivers: driversNext, unassigned };
  }

  if (status === PickupStatus.UNASSIGNED) {
    const { drivers, removed } = removePickupsFromDrivers(columns.drivers, ids);
    if (removed.length === 0) return null;

    const batches = removed.map(({ row, group }) => ({
      pickups: [unassignedFromGroupRow(row, group)],
      lat: group.shipperLat,
      lng: group.shipperLng,
    }));
    const unassignedNext = addPickupsToUnassigned(columns.unassigned, batches);
    return { drivers, unassigned: unassignedNext };
  }

  return null;
}
